import { Box, Button, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { CheckCircle, ErrorOutline } from "@mui/icons-material";
import { useStyles } from "./style";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "@apollo/client/react";
import { WORKFLOW_STATE } from "utils/queries";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "store";
import { logger } from "utils/logger";
import {
  NEW_LEARNING_ACTION_TYPE,
  NEW_LEARNING_ALERT_TYPE,
  showAction,
  LoadingPhase,
} from "../../../shared/newLearningHelper";
import { WorkflowStageEnum } from "utils/types";
import LoadingState from "../../product-recommendations/loading-state";

const POLLING_INTERVAL_MS = 5000;

interface WorkflowState {
  workflowStage?: string;
  learnerId?: string;
  appianReferenceId?: string;
}

interface WorkflowStateResponse {
  workflowState: {
    success: boolean;
    workflowId?: string;
    message?: string;
    workflowState?: WorkflowState;
  };
}

interface RequestSubmittedProps {
  isMobile?: boolean;
  onClose?: () => void;
  setDialogTitle?: (title: string) => void;
}

export const RequestSubmitted: FC<RequestSubmittedProps> = ({
  isMobile = false,
  onClose,
  setDialogTitle,
}) => {
  const classes = useStyles({ isMobile });
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [loadingPhase, setLoadingPhase] = useState<LoadingPhase>(LoadingPhase.Loading);

  const [progressPercent, setProgressPercent] = useState<number>(10);
  const userContext = useSelector((state: RootState) => state.core.userContext);
  const [appianReferenceId, setAppianReferenceId] = useState<string | null>(null);
  const isCompleted = !!appianReferenceId;
  const isError = loadingPhase === LoadingPhase.Error;

  useEffect(() => {
    const isLoading = !appianReferenceId && loadingPhase !== LoadingPhase.Error;
    if (isLoading) {
      setDialogTitle?.(t("request_submitting"));
    } else if (loadingPhase === LoadingPhase.Error) {
      setDialogTitle?.(t("request_failed"));
    } else {
      setDialogTitle?.(t("request_submitted"));
    }
  }, [setDialogTitle, t, appianReferenceId, loadingPhase]);

  const handleWorkflowError = ({
    logMessage,
    stopPolling,
    workflowId,
    message,
    workflowStage,
    alertMessage,
    error,
  }: {
    logMessage: string;
    stopPolling?: () => void;
    workflowId?: string | null;
    message?: string | null;
    workflowStage?: WorkflowStageEnum | null;
    alertMessage?: string;
    error?: string;
  }) => {
    stopPolling?.();
    setLoadingPhase(LoadingPhase.Error);
    logger.error(logMessage, {
      workflowId,
      message,
      workflowStage,
      error,
    });
    showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
      type: NEW_LEARNING_ALERT_TYPE.ERROR,
      title: t("request_failed"),
      message: alertMessage || message || t("request_submitted_title_error"),
    });
  };

  const [fetchWorkflowState, { data, loading, stopPolling, startPolling, error: queryError }] =
    useLazyQuery<WorkflowStateResponse>(WORKFLOW_STATE, {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    });

  // Apollo Client v4: Handle data/error with useEffect instead of onCompleted/onError
  useEffect(() => {
    if (data) {
      const workflowStateResponse = data?.workflowState;

      if (!workflowStateResponse) return;

      const { success, workflowState, workflowId, message } = workflowStateResponse;
      const stage = workflowState?.workflowStage;

      if (
        success !== false &&
        stage !== WorkflowStageEnum.Completed &&
        stage !== WorkflowStageEnum.FailedToSubmitToAppian &&
        stage !== WorkflowStageEnum.Failed
      ) {
        setProgressPercent((prev) => Math.min(90, prev + 12));
      }

      if (
        success === false ||
        workflowState?.workflowStage === WorkflowStageEnum.FailedToSubmitToAppian ||
        workflowState?.workflowStage === WorkflowStageEnum.Failed ||
        workflowState?.workflowStage === WorkflowStageEnum.Cancelled
      ) {
        handleWorkflowError({
          logMessage: "Workflow state returned unsuccessful response",
          stopPolling,
          workflowId,
          message,
          workflowStage: workflowState?.workflowStage as WorkflowStageEnum,
        });
      } else if (workflowState?.workflowStage === WorkflowStageEnum.Completed) {
        if (workflowState.appianReferenceId == null) {
          handleWorkflowError({
            logMessage: "Workflow completed without appianReferenceId",
            stopPolling,
            workflowId,
            message,
            workflowStage: workflowState?.workflowStage as WorkflowStageEnum,
          });
          return;
        }

        stopPolling?.();
        setProgressPercent(100);
        setAppianReferenceId(workflowState.appianReferenceId);
      }
    }
  }, [data, dispatch, t]);

  useEffect(() => {
    if (queryError) {
      handleWorkflowError({
        logMessage: "Failed to fetch workflow state",
        stopPolling,
        alertMessage: t("work-flow-error-message"),
        error: queryError.message,
      });
    }
  }, [queryError, dispatch, t]);

  useEffect(() => {
    setProgressPercent(10);
    fetchWorkflowState();
    startPolling(POLLING_INTERVAL_MS);
    return () => stopPolling?.();
  }, [fetchWorkflowState, startPolling, stopPolling]);

  if (loading || (!isCompleted && !isError)) {
    return (
      <Box className={classes.container}>
        <LoadingState
          classes={classes}
          t={t}
          progress={loadingPhase}
          progressPercent={progressPercent}
          titleText={t("request_submitted_loading_title")}
          statusText={t("request_submitted_loading_description")}
        />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.contentWrapper}>
        <Box className={classes.iconWrapper}>
          <Box className={isError ? classes.errorIconBox : classes.iconBox}>
            {isError ? (
              <ErrorOutline className={classes.errorIcon} />
            ) : (
              <CheckCircle className={classes.successIcon} />
            )}
          </Box>
        </Box>
        <Box>
          <Typography variant="h2" className={classes.title}>
            {isError ? t("request_submitted_title_error") : t("request_submitted_title")}
          </Typography>
          {isError ? (
            <Typography className={classes.description}>
              {t("request_submitted_description_error")}
            </Typography>
          ) : (
            <>
              <Typography className={classes.description}>
                {t("request_submitted_description")}
              </Typography>
              <Box className={classes.referenceBox}>
                <Typography className={classes.referenceText}>{appianReferenceId}</Typography>
              </Box>
            </>
          )}
          {!isError && (
            <Typography className={classes.confirmationText}>
              {t("request_submitted_confirmation", {
                learnerEmail: userContext.userContext.learnerEmail,
              })}
            </Typography>
          )}
        </Box>
        <Box className={classes.buttonContainer}>
          <Button variant="contained" className={classes.primaryButton} onClick={onClose}>
            {t("close")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
